import { getDoc, doc } from 'firebase/firestore'
import type {
    FaviconStorageItem,
    FaviconStorageItem as SharedFaviconStorageItem,
} from '~common/favicon-fetcher/service'
import { StorageModuleDeps } from '../storage/types'

export interface FaviconStorageDeps extends StorageModuleDeps {}

export class FaviconStorageModule {
    private readonly collectionName = 'favicons'

    constructor(private readonly deps: FaviconStorageDeps) {}

    async initialize(): Promise<void> {
        return
    }
    async getFavicon(domain: string): Promise<FaviconStorageItem | null> {
        const ref = doc(
            this.deps.db,
            this.collectionName,
            toTopLevelDomain(domain),
        )

        const snap = await getDoc(ref)
        if (!snap.exists()) return null
        const data = snap.data() as {
            blob?: string
            mimeType?: string
            fetchedAt?: number
        }
        return {
            domain: toTopLevelDomain(domain),
            blob: data.blob ?? null,
            mimeType: data.mimeType ?? null,
            fetchedAt: data.fetchedAt ?? 0,
        }
    }

    async getFaviconDataUrl(domain: string): Promise<string | null> {
        const item = await this.getFavicon(domain)
        if (!item?.blob) return null
        const mimeType = item.mimeType ?? 'image/png'
        return `data:${mimeType};base64,${item.blob}`
    }

    async getFavicons(
        domains: string[],
    ): Promise<Record<string, SharedFaviconStorageItem>> {
        const results: Record<string, SharedFaviconStorageItem> = {}
        for (const domain of domains) {
            const item = await this.getFavicon(domain)
            if (item) results[domain] = item
        }
        return results
    }

    async getFaviconsDataUrls(
        domains: string[],
    ): Promise<Record<string, string | null>> {
        const items = await this.getFavicons(domains)
        const results: Record<string, string | null> = {}
        for (const domain of domains) {
            const tld = toTopLevelDomain(domain)
            const item = items[tld]
            if (item?.blob) {
                const mimeType = item.mimeType ?? 'image/png'
                results[domain] = `data:${mimeType};base64,${item.blob}`
            } else {
                results[domain] = null
            }
        }
        return results
    }
}

function toTopLevelDomain(domain: string): string {
    const parts = domain.split('.').filter(Boolean)
    if (parts.length <= 2) return domain
    return parts.slice(-2).join('.')
}
